import { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import Image from '@base/Image';
import { Spacings, mediaQuery, Fonts } from '@styles/index';
import Typography from 'aquilla/core/atoms/Typography';
import Pill from 'aquilla/core/molecules/Pill';
import ShowCase from 'aquilla/core/molecules/ShowCase';
import { segment } from '@actions/analytics';
import useCentreInfo from '@api/hooks/citySpecificOfflineCentres/useCentreInfo';
import { useRouter } from 'next/router';
import useOfflineCentreStore from '@stores/city-specific-offline-centres';
import useWindowSize from '@hooks/useWindowSize';
import GridContainer from './CommonComponents';
import BatchCard from './BatchCard';
import { CENTRE_BATCH_FILTER_CLICKED } from './constants';

const StyGridContainer = styled(GridContainer)`
  margin-top: ${Spacings.SPACING_16B};
  margin-bottom: ${Spacings.SPACING_16B};
  ${mediaQuery.uptoMobile} {
    margin-top: auto;
    padding-right: 0px !important;
    padding-left: 0px !important;
    margin-bottom: auto;
  }
`;

const StyH3 = styled(Typography)`
  ${mediaQuery.uptoMobile} {
    padding-left: ${Spacings.SPACING_4B};
  }
`;
const StyH5 = styled(Typography)`
  margin-left: ${Spacings.SPACING_1B};
  font-weight: ${Fonts.LIGHT};
`;
const FeatureWrapper = styled.div`
  display: flex;
  margin-right: ${Spacings.SPACING_6B};
  ${mediaQuery.uptoMobile} {
    margin-right: 0px !important;
    margin-bottom: ${Spacings.SPACING_3B};
    padding-left: ${Spacings.SPACING_4B};
  }
`;

const AllFeatureWrapper = styled.div`
  display: flex;
  margin-top: ${Spacings.SPACING_3B};
  margin-bottom: ${Spacings.SPACING_6B};
  ${mediaQuery.uptoMobile} {
    margin-bottom: ${Spacings.SPACING_3B};
    flex-direction: column;
  }
`;

const Container = styled.div`
  grid-column: span 24;
  display: flex;
  flex-direction: column;
  overflow-x: scroll;
  overflow-y: hidden;
  ${mediaQuery.uptoMobile} {
    width: 100%;
  }
`;

const PillWrapper = styled.div`
  cursor: pointer;
  flex-shrink: 0;
`;

const PillsWrapper = styled.div`
  display: flex;
  grid-gap: 0 ${Spacings.SPACING_4B};
  overflow: scroll;
  ::-webkit-scrollbar {
    display: none;
  }

  ${mediaQuery.uptoTablet} {
    > * {
      :first-of-type {
        padding-left: ${Spacings.SPACING_8B};
      }
      :last-of-type {
        padding-right: ${Spacings.SPACING_8B};
      }
    }
  }

  ${mediaQuery.uptoMobile} {
    grid-gap: 0 ${Spacings.SPACING_3B};
    > * {
      :first-of-type {
        padding-left: ${Spacings.SPACING_4B};
      }
      :last-of-type {
        padding-right: ${Spacings.SPACING_4B};
      }
    }
  }
`;

const StyShowCase = styled(ShowCase)`
  & > .aquilla-showcase-section-header {
    position: relative;
    padding: 0px;
    margin: 0px;
    margin-bottom: ${Spacings.SPACING_8B};
    ${mediaQuery.uptoMobile} {
      margin-bottom: ${Spacings.SPACING_6B};
    }
    & > .aquilla-showcase-section-header-actions {
      position: absolute;
      top: 0;
      right: 0;
      width: auto;
    }
    & > .aquilla-showcase-section-header-heading {
      width: 100%;
    }
  }
`;

const Circle = styled(Image)`
  background: ${({ color }) => color};
  border-radius: 12px;
`;

const PillTitle = styled(Typography)`
  font-size: ${Fonts.H6};
  font-weight: ${Fonts.SEMIBOLD};
  line-height: ${Spacings.SPACING_5B};
`;

const Batches = ({ data, batchesRef, batchesRefFilterRef }) => {
  const {
    batchesList,
    header,
    descriptionCtAs,
    showClassPills,
    batchesChipIcon
  } = data;
  const classKeys = Object.keys(batchesList);
  const classLabels = [];
  const allBatchesOfAllClasses = [];
  const {
    query: { goalUID: routeGoalUID, centreUID }
  } = useRouter();
  const { centreInfo } = useCentreInfo(routeGoalUID, centreUID);
  const { centreName, cityName } = centreInfo || {};
  const { isBatchesOnTop, isBatchesVisibleZus } = useOfflineCentreStore();

  classKeys.forEach((key) => {
    if (key !== 'allClasses') {
      const { batchBannerColor, batchIconsAccentColor, batches } =
        batchesList[key]; // add pill icon prop here
      const batchPillIcon = '';
      classLabels.push({
        label: batchesList[key].label,
        key,
        accentColor: batchIconsAccentColor,
        pillIcon: batchPillIcon
      });
      const newBatches = batches.map((batch) => {
        return {
          ...batch,
          batchBannerColor,
          batchIconsAccentColor
        };
      });
      allBatchesOfAllClasses.push(...newBatches);
    }
  });
  const [selectedClass, setSelectedClass] = useState(classKeys[0]);

  useEffect(() => {
    setSelectedClass(classKeys[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchesList]);

  batchesList.allClasses = {
    batches: [...allBatchesOfAllClasses]
  };
  const { isMobile } = useWindowSize();

  const renderFeatures = () => {
    const features = descriptionCtAs.map((feature) => {
      return (
        <FeatureWrapper key={feature}>
          <Image
            src="/offline-centres/checked.svg"
            alt="feature-icon"
            width="20px"
            height="20px"
          />
          <StyH5 variant="h6">{feature}</StyH5>
        </FeatureWrapper>
      );
    });
    return <AllFeatureWrapper>{features}</AllFeatureWrapper>;
  };
  const renderClassPills = (isStandAlone = false) => {
    const pills = classLabels.map((batchClass) => {
      const handleClassPillClick = () => {
        setSelectedClass(batchClass.key);
        segment.track(CENTRE_BATCH_FILTER_CLICKED, {
          centre_name: centreName,
          city: cityName,
          goal_uid: routeGoalUID,
          batch_filtered: batchClass.label
        });
      };
      return (
        <PillWrapper key={batchClass.label} onClick={handleClassPillClick}>
          <Pill
            title={<PillTitle variant="h5">{batchClass.label}</PillTitle>}
            isSelected={batchClass.key === selectedClass}
            icon={
              <Circle
                color={batchClass.accentColor}
                src={batchesChipIcon}
                width="24px"
                height="24px"
              />
            } // this should be coming from CMS
          />
        </PillWrapper>
      );
    });
    return (
      <PillsWrapper
        ref={isStandAlone ? null : batchesRefFilterRef}
        $isBatchesOnTop={isBatchesOnTop}
        $isStandAlone={isStandAlone}
        $isMobile={isMobile}
        $isBatchesVisibleZus={isBatchesVisibleZus}
      >
        {pills}
      </PillsWrapper>
    );
  };
  const renderBatchesOfAClass = () => {
    const selectedClassBatches = batchesList[selectedClass];

    const { batchBannerColor, batchIconsAccentColor, label } =
      selectedClassBatches || {};
    const batchCard = selectedClassBatches?.batches.map((batch) => {
      return (
        <BatchCard
          data={batch}
          classType={selectedClass}
          bannerColor={batchBannerColor}
          iconColor={batchIconsAccentColor}
          key={batch.uid}
          classLabel={label}
        />
      );
    });
    return batchCard;
  };
  return (
    <>
      {isBatchesOnTop && isBatchesVisibleZus && renderClassPills(true)}
      <StyGridContainer>
        <Container ref={batchesRef}>
          {showClassPills !== false && (
            <>
              <StyH3 variant="h3">{header}</StyH3>
              {renderFeatures()}
            </>
          )}
          <StyShowCase
            itemsPerSlide={3}
            title={
              showClassPills === false ? (
                <>
                  <StyH3 variant="h3">{header}</StyH3>
                  {renderFeatures()}
                </>
              ) : (
                renderClassPills()
              )
            }
          >
            {renderBatchesOfAClass()}
          </StyShowCase>
        </Container>
      </StyGridContainer>
    </>
  );
};

export default Batches;
